import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactElement, useState } from 'react'
import { Dropdown } from '~/components/Form'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'
import {
  formatDateShort,
  formatRelativeDateLabel,
  parseRelativeDate
} from '~/utils/date'
import { DateRangeEditor } from './editors/DateRangeEditor'
import { TextFilterEditor } from './editors/TextFilterEditor'
import {
  chip,
  chipBody,
  chipLabel,
  chipPlaceholder,
  chipRemove,
  chipValue,
  chipValueMore,
  editorWrapper,
  popover
} from './FilterBar.css'
import type { FilterDefinition, FiltersState } from './types'

type FilterChipProps = {
  definition: FilterDefinition
  filters: FiltersState
  onChange: (filters: FiltersState) => void
  onRemove: () => void
  initiallyOpen?: boolean
  onClose?: () => void
  onOpenChange?: (open: boolean) => void
}

const parseMultiSelectValue = (value: string): string[] =>
  value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)

const getDisplayValue = (
  def: FilterDefinition,
  filters: FiltersState
): string | ReactElement | null => {
  if (def.type === 'select') {
    const value = filters[def.key]
    if (!value) return null
    const options =
      typeof def.options === 'function' ? def.options() : def.options
    if (def.multiSelect) {
      const selected = parseMultiSelectValue(value)
      if (selected.length === 0) return null
      const labels = selected
        .map((v) => options.find((o) => o.value === v)?.label ?? v)
        .filter((label): label is string => Boolean(label))
      if (labels.length === 0) return null
      if (labels.length === 1) return labels[0] ?? null
      return (
        <Tooltip text={labels.join(', ')} direction="top">
          {labels[0]}{' '}
          <span className={chipValueMore}>+{labels.length - 1}</span>
        </Tooltip>
      )
    }
    return options.find((o) => o.value === value)?.label ?? value
  }
  if (def.type === 'autocomplete') {
    const value = filters[def.key]
    if (!value) return null
    return def.renderValue(value)
  }
  if (def.type === 'text') {
    const value = filters[def.key]
    if (!value) return null
    return def.renderValue ? def.renderValue(value) : value
  }
  if (def.type === 'dateRange') {
    const after = filters[def.keyAfter]
    const before = filters[def.keyBefore]
    if (!after && !before) return null
    const formatBound = (value: string): string => {
      const relative = parseRelativeDate(value)
      return relative
        ? formatRelativeDateLabel(relative.count, relative.unit)
        : formatDateShort(new Date(value))
    }
    if (after && before)
      return (
        <>
          {parseRelativeDate(after) ? 'Il y a' : ''} entre
          {parseRelativeDate(after) ? ' ' : ' le '}
          {formatBound(after)} et {parseRelativeDate(before) ? ' ' : ' le '}
          {formatBound(before)}
        </>
      )
    if (after) {
      return `${parseRelativeDate(after) ? 'il y a moins de' : ' après le '} ${formatBound(after)}`
    }
    if (before) {
      const prefix = parseRelativeDate(before) ? 'il y a plus de' : "jusqu'au"
      return `${prefix} ${formatBound(before)}`
    }
    return null
  }
  return null
}

export const FilterChip = ({
  definition,
  filters,
  onChange,
  onRemove,
  initiallyOpen = false,
  onClose,
  onOpenChange
}: FilterChipProps): ReactElement => {
  const [open, setOpen] = useState(initiallyOpen)

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (newOpen) => {
      setOpen(newOpen)
      onOpenChange?.(newOpen)
      if (!newOpen) {
        onClose?.()
      }
    },
    strategy: 'fixed',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start'
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss
  ])
  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  const displayValue = getDisplayValue(definition, filters)

  const handleConfirm = (newFilters: FiltersState) => {
    onChange(newFilters)
    setOpen(false)
  }

  const renderEditor = (): ReactElement | null => {
    if (definition.type === 'select') {
      const value = filters[definition.key] ?? ''
      const options =
        typeof definition.options === 'function'
          ? definition.options()
          : definition.options
      const selectedValues = parseMultiSelectValue(value)
      const defaultValueOptions = selectedValues.map((selectedValue) => {
        const foundOption = options.find(
          (option) => option.value === selectedValue
        )
        return foundOption ?? { label: selectedValue, value: selectedValue }
      })
      const defaultValueOption =
        options.find((option) => option.value === value) ??
        (value ? { label: value, value } : undefined)
      const dropdownModeProps = definition.multiSelect
        ? {
            multiple: true as const,
            defaultValueOptions,
            onChange: (nextValues: string[]) => {
              if (nextValues.includes('')) {
                handleConfirm({ ...filters, [definition.key]: '' })
                return
              }
              handleConfirm({
                ...filters,
                [definition.key]: nextValues.filter(Boolean).join(',')
              })
            }
          }
        : {
            multiple: false as const,
            defaultValueOption,
            onChange: (nextValue: string) => {
              handleConfirm({ ...filters, [definition.key]: nextValue })
            }
          }
      return (
        <Dropdown
          key={`${definition.key}-${value}`}
          inlineList
          small
          fullWidth
          options={options}
          searchable={definition.searchable ?? false}
          emptyLabel={definition.emptyLabel}
          {...dropdownModeProps}
        />
      )
    }
    if (definition.type === 'autocomplete') {
      return definition.renderEditor({
        value: filters[definition.key] ?? '',
        onChange: (value) => {
          handleConfirm({ ...filters, [definition.key]: value })
        },
        onClose: () => setOpen(false)
      })
    }
    if (definition.type === 'text') {
      return (
        <TextFilterEditor
          value={filters[definition.key] ?? ''}
          placeholder={definition.placeholder}
          transform={definition.transform}
          onChange={(value) =>
            handleConfirm({ ...filters, [definition.key]: value })
          }
          onClose={() => setOpen(false)}
        />
      )
    }
    if (definition.type === 'dateRange') {
      return (
        <DateRangeEditor
          definition={definition}
          filters={filters}
          onChange={onChange}
        />
      )
    }
    return null
  }

  return (
    <>
      <div ref={refs.setReference} className={chip} {...getReferenceProps()}>
        <span className={chipBody}>
          <span className={chipLabel}>{definition.label}</span>
          {displayValue !== null ? (
            <span className={chipValue}>{displayValue}</span>
          ) : (
            <span className={chipPlaceholder}>Sélectionner</span>
          )}
        </span>
        <span
          className={chipRemove}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </span>
      </div>
      <FloatingPortal>
        {isMounted && (
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 99 }}
            {...getFloatingProps()}
          >
            <div className={clsx(themeClass, popover)} style={transitionStyles}>
              <div className={editorWrapper}>{renderEditor()}</div>
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  )
}
